import Placeholder from '@tiptap/extension-placeholder';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Code,
  FileCode,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Maximize2,
  Minus,
  Quote,
  Strikethrough,
} from 'lucide-react';
import * as React from 'react';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { cn } from '../../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../molecules/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '../../molecules/drawer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MarkdownInputProps {
  /** Current markdown value (controlled). */
  value?: string;
  /** Called with the new markdown string on every edit. */
  onChange?: (value: string) => void;
  /** Called when the editor loses focus. */
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  className?: string;
  /** Minimum height of the editable area. Defaults to `'160px'`. */
  minHeight?: string;
  /**
   * When true the editor grows to fill its flex container instead of using
   * `minHeight`. Use inside fixed-height parents (e.g. the expand dialog/drawer).
   */
  fill?: boolean;
  /**
   * Show the expand button in the toolbar. Defaults to `true`.
   * Set to `false` when already rendered inside the expanded overlay to
   * prevent nesting.
   */
  expandable?: boolean;
  /** Label shown as the overlay title when expanded. Defaults to `'Edit'`. */
  expandLabel?: string;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'false' | 'true' | 'grammar' | 'spelling';
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────

function ToolbarButton({
  onClick,
  isActive,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      onMouseDown={(e) => {
        e.preventDefault(); // keep editor focus
        onClick();
      }}
      className={cn(
        'flex size-7 items-center justify-center rounded text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        isActive && 'bg-accent text-accent-foreground',
      )}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <span className="mx-0.5 h-4 w-px shrink-0 bg-border" aria-hidden />;
}

function Toolbar({ editor, onExpand }: { editor: Editor | null; onExpand?: () => void }) {
  if (!editor) return null;

  const btn = (title: string, icon: React.ReactNode, isActive: boolean, run: () => unknown) => (
    <ToolbarButton key={title} title={title} isActive={isActive} onClick={() => run()}>
      {icon}
    </ToolbarButton>
  );

  return (
    <div className="flex items-center gap-0.5 border-b border-input px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-0.5 flex-1">
        {btn('Bold', <Bold className="size-3.5" />, editor.isActive('bold'), () =>
          editor.chain().focus().toggleBold().run(),
        )}
        {btn('Italic', <Italic className="size-3.5" />, editor.isActive('italic'), () =>
          editor.chain().focus().toggleItalic().run(),
        )}
        {btn(
          'Strikethrough',
          <Strikethrough className="size-3.5" />,
          editor.isActive('strike'),
          () => editor.chain().focus().toggleStrike().run(),
        )}

        <ToolbarSep />

        {btn(
          'Heading 1',
          <Heading1 className="size-3.5" />,
          editor.isActive('heading', { level: 1 }),
          () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
        )}
        {btn(
          'Heading 2',
          <Heading2 className="size-3.5" />,
          editor.isActive('heading', { level: 2 }),
          () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        )}
        {btn(
          'Heading 3',
          <Heading3 className="size-3.5" />,
          editor.isActive('heading', { level: 3 }),
          () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
        )}

        <ToolbarSep />

        {btn('Bullet List', <List className="size-3.5" />, editor.isActive('bulletList'), () =>
          editor.chain().focus().toggleBulletList().run(),
        )}
        {btn(
          'Numbered List',
          <ListOrdered className="size-3.5" />,
          editor.isActive('orderedList'),
          () => editor.chain().focus().toggleOrderedList().run(),
        )}

        <ToolbarSep />

        {btn('Inline Code', <Code className="size-3.5" />, editor.isActive('code'), () =>
          editor.chain().focus().toggleCode().run(),
        )}
        {btn('Code Block', <FileCode className="size-3.5" />, editor.isActive('codeBlock'), () =>
          editor.chain().focus().toggleCodeBlock().run(),
        )}

        <ToolbarSep />

        {btn('Blockquote', <Quote className="size-3.5" />, editor.isActive('blockquote'), () =>
          editor.chain().focus().toggleBlockquote().run(),
        )}
        {btn('Horizontal Rule', <Minus className="size-3.5" />, false, () =>
          editor.chain().focus().setHorizontalRule().run(),
        )}
      </div>

      {onExpand && (
        <>
          <ToolbarSep />
          <ToolbarButton title="Expand editor" onClick={onExpand}>
            <Maximize2 className="size-3.5" />
          </ToolbarButton>
        </>
      )}
    </div>
  );
}

// ─── Shared inner editor (used in both the expand overlay and inline) ─────────

function EditorInner({
  value,
  onChange,
  onBlur,
  disabled,
  readOnly,
  placeholder,
  minHeight,
  fill,
  expandable,
  className,
  forwardedRef,
  ...props
}: Omit<MarkdownInputProps, 'expandLabel'> & {
  forwardedRef?: React.Ref<HTMLDivElement>;
  onExpand?: () => void;
}) {
  const isReadOnly = disabled || readOnly;
  const { onExpand, ...divProps } = props as { onExpand?: () => void } & typeof props;

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value,
    editable: !isReadOnly,
    editorProps: {
      attributes: { class: 'outline-none' },
    },
    onUpdate: ({ editor: e }) => {
      const md = (e.storage as unknown as { markdown: MarkdownStorage }).markdown.getMarkdown();
      onChange?.(md);
    },
    onBlur: () => onBlur?.(),
  });

  const mdStorage = (editor?.storage as unknown as { markdown?: MarkdownStorage } | undefined)
    ?.markdown;

  React.useEffect(() => {
    editor?.setEditable(!isReadOnly, false);
  }, [editor, isReadOnly]);

  const prevRef = React.useRef(value ?? '');
  React.useEffect(() => {
    if (!editor || !mdStorage) return;
    const current = mdStorage.getMarkdown();
    if ((value ?? '') !== prevRef.current && (value ?? '') !== current) {
      editor.commands.setContent(value ?? '');
    }
    prevRef.current = value ?? '';
  }, [value, editor, mdStorage]);

  return (
    <div
      ref={forwardedRef}
      className={cn(
        'ar-markdown-editor',
        fill ? 'flex flex-col overflow-hidden' : 'overflow-hidden',
        'rounded-[min(var(--field-radius,var(--radius)),0.75rem)] border border-input bg-input-bg',
        'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background',
        'aria-[invalid=true]:border-destructive',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      {...divProps}
    >
      {!isReadOnly && <Toolbar editor={editor} onExpand={expandable ? onExpand : undefined} />}
      <EditorContent
        editor={editor}
        style={fill ? undefined : { minHeight }}
        className={cn(
          'px-3 py-2 text-base sm:text-sm text-foreground',
          // Heading spacing — margin-top creates section breathing room,
          // margin-bottom tightens the heading to its own content.
          // [&_:first-child] resets top margin so the editor doesn't open with dead space.
          '[&_h1]:mt-8 [&_h1]:mb-2',
          '[&_h2]:mt-6 [&_h2]:mb-1.5',
          '[&_h3]:mt-4 [&_h3]:mb-1',
          '[&_:first-child]:mt-0',
          fill && 'flex-1 overflow-auto',
          isReadOnly && 'pointer-events-none',
        )}
      />
    </div>
  );
}

// ─── MarkdownInput ────────────────────────────────────────────────────────────

/**
 * Rich-text form input that stores its value as a markdown string.
 *
 * Powered by Tiptap + `tiptap-markdown`. The `value`/`onChange` contract is
 * identical to a `<textarea>` — pass a markdown string in, receive a markdown
 * string out — making it a drop-in replacement inside `FormField → FormControl`.
 *
 * In read-only / disabled mode the toolbar is hidden and the content is
 * rendered as-is (no cursor).
 *
 * The toolbar includes an expand button (unless `expandable={false}`) that:
 * - On mobile: opens a near-fullscreen bottom Drawer
 * - On desktop: opens a large centered Dialog
 */
const MarkdownInput = React.forwardRef<HTMLDivElement, MarkdownInputProps>(
  (
    {
      value = '',
      onChange,
      onBlur,
      disabled = false,
      readOnly = false,
      placeholder = 'Write something…',
      className,
      minHeight = '160px',
      fill = false,
      expandable = true,
      expandLabel = 'Edit',
      ...props
    },
    ref,
  ) => {
    const [expandedOpen, setExpandedOpen] = React.useState(false);
    // Fine pointer = desktop → Dialog. Coarse pointer = touch → Drawer.
    const isDesktop = useMediaQuery('(hover: hover) and (pointer: fine)');

    const sharedEditorProps = {
      value,
      onChange,
      onBlur,
      disabled,
      readOnly,
      placeholder,
      minHeight,
      fill,
    };

    const expandedEditor = (
      <EditorInner {...sharedEditorProps} expandable={false} fill className="flex-1" />
    );

    return (
      <>
        <EditorInner
          {...sharedEditorProps}
          expandable={expandable}
          className={className}
          forwardedRef={ref}
          onExpand={expandable ? () => setExpandedOpen(true) : undefined}
          {...props}
        />

        {expandable && isDesktop && (
          <Dialog open={expandedOpen} onOpenChange={setExpandedOpen}>
            <DialogContent className="flex flex-col gap-0 p-0 max-w-[min(calc(100vw-4rem),1200px)] h-[min(calc(100vh-4rem),96vh)]">
              <DialogHeader className="shrink-0 border-b border-border px-4 py-3">
                <DialogTitle className="text-base font-semibold">{expandLabel}</DialogTitle>
              </DialogHeader>
              <div className="flex min-h-0 flex-1 flex-col p-3">{expandedEditor}</div>
            </DialogContent>
          </Dialog>
        )}

        {expandable && !isDesktop && (
          <Drawer open={expandedOpen} onOpenChange={setExpandedOpen}>
            <DrawerContent className="h-[92svh] flex flex-col overflow-hidden">
              <DrawerHeader className="shrink-0 border-b border-border px-4 py-3 text-left">
                <DrawerTitle className="text-base font-semibold">{expandLabel}</DrawerTitle>
              </DrawerHeader>
              <div className="flex min-h-0 flex-1 flex-col p-3">{expandedEditor}</div>
            </DrawerContent>
          </Drawer>
        )}
      </>
    );
  },
);
MarkdownInput.displayName = 'MarkdownInput';

export { MarkdownInput };
